import React from 'react';
import { DashboardKpis } from '../types';
import {
  CurrencyEuroIcon,
  BellAlertIcon,
  ChartBarIcon,
  ClockIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';

interface DashboardKpiCardsProps {
  kpis: DashboardKpis;
  onOuvrirGraphique?: (type: 'marge' | 'sante') => void;
}

const fmt = (n: number) => Math.abs(Math.round(n)).toLocaleString('fr-FR');

const DashboardKpiCards: React.FC<DashboardKpiCardsProps> = ({ kpis, onOuvrirGraphique }) => {
  const isMatPositif = kpis.matGlobal >= 0;

  const cards = [
    {
      label: 'Marge à Terminaison',
      sublabel: isMatPositif ? 'MAT portfolio' : 'MAT portfolio négatif',
      value: `${isMatPositif ? '+' : '-'}${fmt(kpis.matGlobal)} €`,
      icon: CurrencyEuroIcon,
      valueColor: isMatPositif ? 'text-emerald-600' : 'text-red-500',
      indicator: isMatPositif ? 'bg-emerald-500' : 'bg-red-500',
      onClick: () => onOuvrirGraphique?.('marge'),
      clickable: true,
      tooltip: (
        <>
          <p className="font-semibold text-violet-300 mb-1.5">Marge À Terminaison (MAT)</p>
          <p className="mb-1.5">Projection de la marge finale en extrapolant les coûts engagés par rapport à l'avancement physique.</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-emerald-400 font-medium">+ = bénéfice</span>
            <span className="text-gray-500">|</span>
            <span className="text-red-400 font-medium">- = perte</span>
          </div>
        </>
      ),
    },
    {
      label: 'Alertes critiques',
      sublabel: kpis.alertesTotal > kpis.alertesCritiques
        ? `${kpis.alertesTotal} alertes au total`
        : kpis.incidentsOuverts > 0
          ? `dont ${kpis.incidentsOuverts} incident${kpis.incidentsOuverts > 1 ? 's' : ''}`
          : 'Actions urgentes',
      value: kpis.alertesCritiques.toString(),
      icon: BellAlertIcon,
      valueColor: kpis.alertesCritiques > 0 ? 'text-red-500' : 'text-emerald-600',
      indicator: kpis.alertesCritiques > 0 ? 'bg-red-500' : 'bg-emerald-500',
      clickable: false,
      tooltip: (
        <>
          <p className="font-semibold text-violet-300 mb-1.5">Alertes critiques (rouge)</p>
          <ul className="space-y-1 list-none">
            <li><span className="text-red-400">&#9679;</span> Dépassement budget &gt; 10%</li>
            <li><span className="text-red-400">&#9679;</span> Lot en retard &gt; 4 semaines</li>
            <li><span className="text-red-400">&#9679;</span> Incident de sécurité</li>
            <li><span className="text-red-400">&#9679;</span> Projet à risque ou en pause</li>
          </ul>
          <p className="mt-1.5 text-gray-400">Les alertes orange (vigilance) sont comptées dans le total.</p>
        </>
      ),
    },
    {
      label: 'Performance globale',
      sublabel: 'Score portfolio',
      value: `${kpis.performanceGlobale}/100`,
      icon: ChartBarIcon,
      valueColor: kpis.performanceGlobale >= 70
        ? 'text-emerald-600'
        : kpis.performanceGlobale >= 40
          ? 'text-amber-600'
          : 'text-red-500',
      indicator: kpis.performanceGlobale >= 70
        ? 'bg-emerald-500'
        : kpis.performanceGlobale >= 40
          ? 'bg-amber-500'
          : 'bg-red-500',
      clickable: false,
      tooltip: (
        <>
          <p className="font-semibold text-violet-300 mb-1.5">Score moyen /100</p>
          <p className="mb-1.5">Chaque projet part de 100 pts :</p>
          <ul className="space-y-0.5 list-none">
            <li><span className="text-red-400 font-medium">-4</span> par % de dépassement budget</li>
            <li><span className="text-red-400 font-medium">-8</span> par lot en retard</li>
            <li><span className="text-red-400 font-medium">-15</span> si projet à risque</li>
            <li><span className="text-amber-400 font-medium">-10</span> si projet en pause</li>
            <li><span className="text-emerald-400 font-medium">+5</span> si terminé dans le budget</li>
          </ul>
        </>
      ),
    },
    {
      label: 'Respect planning',
      sublabel: 'Projets dans les temps',
      value: `${kpis.respectPlanning}%`,
      icon: ClockIcon,
      valueColor: kpis.respectPlanning >= 80
        ? 'text-emerald-600'
        : kpis.respectPlanning >= 50
          ? 'text-amber-600'
          : 'text-red-500',
      indicator: kpis.respectPlanning >= 80
        ? 'bg-emerald-500'
        : kpis.respectPlanning >= 50
          ? 'bg-amber-500'
          : 'bg-red-500',
      clickable: false,
      tooltip: (
        <>
          <p className="font-semibold text-violet-300 mb-1.5">Respect du planning</p>
          <p>% de projets actifs dont <span className="text-white font-medium">aucun lot n'est en retard</span> par rapport aux échéances prévisionnelles.</p>
          <div className="mt-1.5 text-gray-400">
            <span className="text-emerald-400 font-medium">&ge; 80%</span> bon
            <span className="mx-1">|</span>
            <span className="text-amber-400 font-medium">&ge; 50%</span> moyen
            <span className="mx-1">|</span>
            <span className="text-red-400 font-medium">&lt; 50%</span> critique
          </div>
        </>
      ),
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cards.map((card) => (
        <div
          key={card.label}
          onClick={card.clickable ? card.onClick : undefined}
          className={`relative bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow ${
            card.clickable ? 'cursor-pointer hover:border-violet-300' : ''
          }`}
        >
          {/* Indicateur couleur latéral */}
          <div className={`absolute top-0 left-0 w-1 h-full rounded-l-xl ${card.indicator}`} />

          <div className="flex items-start justify-between mb-3">
            <div>
              <span className="text-xs font-medium text-gray-500">
                {card.label}
              </span>
              <p className="text-[11px] text-gray-400 mt-0.5">{card.sublabel}</p>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <div className="relative group/kpi">
                <QuestionMarkCircleIcon className="w-4 h-4 text-gray-300 hover:text-violet-500 transition-colors cursor-help" />
                <div className="absolute right-0 top-full mt-1.5 w-72 bg-gray-900 text-white text-[11px] rounded-xl p-3.5 shadow-xl z-30 leading-relaxed opacity-0 invisible group-hover/kpi:opacity-100 group-hover/kpi:visible transition-all duration-200 pointer-events-none group-hover/kpi:pointer-events-auto">
                  {card.tooltip}
                </div>
              </div>
              <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                <card.icon className="w-4.5 h-4.5 text-violet-500" />
              </div>
            </div>
          </div>

          <div className={`text-2xl font-bold ${card.valueColor}`}>
            {card.value}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DashboardKpiCards;
